import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { INQUIRY_TYPES } from "@/data/content";

const FORM_NAME = "contact";

const encode = (data) =>
  Object.keys(data)
    .map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(data[k] ?? ""))
    .join("&");

const EMPTY = {
  name: "",
  company: "",
  email: "",
  phone: "",
  inquiry_type: "",
  message: "",
  website: "",
};

const inputCls =
  "h-12 rounded-sm border-charcoal/20 bg-white px-4 text-sm text-charcoal placeholder:text-charcoal/35 focus-visible:border-navy focus-visible:ring-1 focus-visible:ring-navy focus-visible:ring-offset-0";
const labelCls =
  "text-xs font-medium uppercase tracking-[0.15em] text-charcoal/60";

export const ContactForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.inquiry_type) {
      toast.error("Please select an inquiry type.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/__forms.html", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({
          "form-name": FORM_NAME,
          subject: `New Iconicho inquiry: ${form.inquiry_type}`,
          ...form,
        }),
      });
      if (!res.ok) throw new Error(`Form submit failed: ${res.status}`);
      toast.success("Your inquiry has been sent.", {
        description: "A member of our team will follow up with you shortly.",
      });
      setForm(EMPTY);
    } catch {
      toast.error("Unable to send your inquiry.", {
        description: "Please try again in a moment.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      name={FORM_NAME}
      method="POST"
      data-netlify="true"
      netlify-honeypot="website"
      onSubmit={handleSubmit}
      data-testid="contact-form"
      className="rounded-sm border border-charcoal/10 bg-white p-8 lg:p-12"
    >
      <input type="hidden" name="form-name" value={FORM_NAME} />
      <div className="grid gap-6 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="name" className={labelCls}>
            Name *
          </Label>
          <Input
            id="name"
            data-testid="contact-name-input"
            required
            value={form.name}
            onChange={set("name")}
            placeholder="Your full name"
            className={inputCls}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="company" className={labelCls}>
            Company / Organization
          </Label>
          <Input
            id="company"
            data-testid="contact-company-input"
            value={form.company}
            onChange={set("company")}
            placeholder="Organization name"
            className={inputCls}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email" className={labelCls}>
            Email *
          </Label>
          <Input
            id="email"
            type="email"
            data-testid="contact-email-input"
            required
            value={form.email}
            onChange={set("email")}
            placeholder="you@organization.com"
            className={inputCls}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone" className={labelCls}>
            Phone
          </Label>
          <Input
            id="phone"
            type="tel"
            data-testid="contact-phone-input"
            value={form.phone}
            onChange={set("phone")}
            placeholder="(000) 000-0000"
            className={inputCls}
          />
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label className={labelCls}>Inquiry Type *</Label>
          <Select
            value={form.inquiry_type}
            onValueChange={(v) => setForm((f) => ({ ...f, inquiry_type: v }))}
          >
            <SelectTrigger
              data-testid="contact-inquiry-type-select"
              className="h-12 rounded-sm border-charcoal/20 bg-white px-4 text-sm text-charcoal focus:border-navy focus:ring-1 focus:ring-navy focus:ring-offset-0"
            >
              <SelectValue placeholder="Select an inquiry type" />
            </SelectTrigger>
            <SelectContent className="rounded-sm border-charcoal/15 bg-white">
              {INQUIRY_TYPES.map((t) => (
                <SelectItem
                  key={t}
                  value={t}
                  data-testid={`inquiry-option-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="text-sm"
                >
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="message" className={labelCls}>
            Message *
          </Label>
          <Textarea
            id="message"
            data-testid="contact-message-input"
            required
            rows={6}
            value={form.message}
            onChange={set("message")}
            placeholder="Tell us about your project, contract, or requirement."
            className="rounded-sm border-charcoal/20 bg-white px-4 py-3 text-sm text-charcoal placeholder:text-charcoal/35 focus-visible:border-navy focus-visible:ring-1 focus-visible:ring-navy focus-visible:ring-offset-0"
          />
        </div>
      </div>

      <input
        type="text"
        name="website"
        value={form.website}
        onChange={set("website")}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="pointer-events-none absolute -left-[9999px] h-0 w-0 opacity-0"
      />

      <button
        type="submit"
        data-testid="contact-submit-button"
        disabled={submitting}
        className="mt-8 w-full rounded-sm bg-navy px-8 py-4 text-sm font-medium tracking-wide text-white transition-colors duration-300 hover:bg-charcoal disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        {submitting ? "Sending…" : "Submit Inquiry"}
      </button>
    </form>
  );
};
